<template>
  <div class="max-w-4xl mx-auto space-y-3 mb-24 py-3">
    <!-- School Logo Section -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">School Logo</h2>
      <div class="flex flex-col sm:flex-row items-center gap-3">
        <!-- Logo Preview -->
        <div
          class="relative w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50 hover:border-primary transition-colors cursor-pointer group"
          @click="triggerLogoInput"
          @drop.prevent="handleLogoDrop"
          @dragover.prevent
          @dragenter.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          :class="{ 'border-primary bg-primary/10': isDragging }"
        >
          <img
            v-if="logoPreview"
            :src="logoPreview"
            alt="School Logo"
            class="w-full h-full object-cover"
          />
          <div v-else class="flex flex-col items-center text-gray-400">
            <i class="pi pi-image text-xl mb-1"></i>
            <span class="text-xs">No logo</span>
          </div>
          <!-- Overlay on hover -->
          <div
            class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"
          >
            <i class="pi pi-camera text-white text-lg"></i>
          </div>
          <input
            ref="logoInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleLogoSelect"
          />
        </div>

        <div class="flex-1 text-center sm:text-left">
          <p class="text-sm text-gray-600 mb-2">
            Click or drag an image to upload your school logo.
          </p>
          <p class="text-xs text-gray-400">
            Recommended: Square image, max 2MB (JPG, PNG, WebP)
          </p>
          <Button
            v-if="selectedLogoFile"
            label="Upload Logo"
            icon="pi pi-upload"
            class="mt-3 bg-primary text-white text-sm"
            :loading="isUploadingLogo"
            @click="handleUploadLogo"
            size="small"
          />
        </div>
      </div>
    </div>

    <!-- School Details Section -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">School Information</h2>
      <form @submit.prevent="handleUpdateSchool" class="space-y-3">
        <!-- Row 1: Name & Email -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label for="name" class="block text-sm font-medium text-gray-600 mb-1">
              School Name
            </label>
            <InputText
              id="name"
              disabled
              v-model="school.name"
              class="w-full"
              placeholder="Enter School Name"
              :class="{ 'p-invalid': getErrorMessage('name') }"
            />
            <small v-if="getErrorMessage('name')" class="text-red-500">{{
              getErrorMessage('name')
            }}</small>
          </div>

          <div>
            <label for="email" class="block text-sm font-medium text-gray-600 mb-1">
              School Email
            </label>
            <InputText
              id="email"
              type="email"
              v-model="school.email"
              class="w-full"
              placeholder="Enter School Email"
              :class="{ 'p-invalid': getErrorMessage('email') }"
            />
            <small v-if="getErrorMessage('email')" class="text-red-500">{{
              getErrorMessage('email')
            }}</small>
          </div>
        </div>

        <!-- Row 2: Mobile & Website -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label for="mobile" class="block text-sm font-medium text-gray-600 mb-1">
              Phone Number
            </label>
            <InputText
              id="mobile"
              type="tel"
              v-model="school.mobile"
              class="w-full"
              placeholder="Enter Phone Number"
              :class="{ 'p-invalid': getErrorMessage('mobile') }"
            />
            <small v-if="getErrorMessage('mobile')" class="text-red-500">{{
              getErrorMessage('mobile')
            }}</small>
          </div>

          <div>
            <label for="website" class="block text-sm font-medium text-gray-600 mb-1">
              Website
            </label>
            <InputText
              id="website"
              v-model="school.website"
              class="w-full"
              placeholder="https://www.example.com"
              :class="{ 'p-invalid': getErrorMessage('website') }"
            />
            <small v-if="getErrorMessage('website')" class="text-red-500">{{
              getErrorMessage('website')
            }}</small>
          </div>
        </div>

        <!-- Row 3: Address & Attendance Type -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label for="address" class="block text-sm font-medium text-gray-600 mb-1">
              Address
            </label>
            <InputText
              id="address"
              v-model="school.address"
              class="w-full"
              placeholder="Enter School Address"
              :class="{ 'p-invalid': getErrorMessage('address') }"
            />
            <small v-if="getErrorMessage('address')" class="text-red-500">{{
              getErrorMessage('address')
            }}</small>
          </div>

          <div>
            <label for="attendance_type" class="block text-sm font-medium text-gray-600 mb-1">
              Attendance Type
            </label>
            <Select
              id="attendance_type"
              v-model="school.attendance_type"
              :options="attendanceOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full"
              placeholder="Select Attendance Type"
            />
          </div>
        </div>

        <div class="pt-2">
          <Button
            type="submit"
            label="Save Changes"
            icon="pi pi-check"
            class="bg-primary text-white"
            :loading="isSubmitting"
            size="small"
            data-testid="btn-settings-school-update"
          />
        </div>
      </form>
    </div>

    <!-- Feature Settings -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-2">Feature Settings</h2>
      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-700">Allow students to upload their own photo</p>
            <p class="text-xs text-gray-400">When enabled, students can upload a profile photo from their dashboard settings.</p>
          </div>
          <InputSwitch v-model="allowStudentPhotoUpload" @change="saveFeatureSettings" />
        </div>
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-700">Always show scrollbars</p>
            <p class="text-xs text-gray-400">
              When enabled, scrollbars stay visible across the admin and student portals instead of being hidden.
              Users can still override this for themselves from My Account.
            </p>
          </div>
          <InputSwitch v-model="showScrollbars" @change="saveFeatureSettings" data-testid="switch-show-scrollbars" />
        </div>
        <div v-if="isTertiary" class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-700">Course registration open</p>
            <p class="text-xs text-gray-400">When off, students cannot submit or change course registrations until you reopen it. Staff can still register students manually.</p>
          </div>
          <InputSwitch v-model="courseRegistrationOpen" @change="saveCourseRegistrationSettings" data-testid="switch-course-registration-open" />
        </div>
        <div v-if="isTertiary" class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-700">Require financial clearance for course registration</p>
            <p class="text-xs text-gray-400">Students must settle their bills — or meet your configured payment threshold — before they can submit course registration.</p>
          </div>
          <InputSwitch v-model="requireFinancialClearance" @change="saveCourseRegistrationSettings" data-testid="switch-financial-clearance" />
        </div>
        <FinancialClearanceRules v-if="isTertiary && requireFinancialClearance" />
        <div v-if="isTertiary" class="flex items-center justify-between gap-3">
          <div>
            <p class="text-sm font-medium text-gray-700">Module retake policy</p>
            <p class="text-xs text-gray-400">Controls when students can register a fresh attempt of a module they have already sat. "Later semester only" is the traditional rule; "Same or later semester" lets students immediately re-sit within the same semester.</p>
          </div>
          <Select
            v-model="retakePolicy"
            :options="retakePolicyOptions"
            optionLabel="label"
            optionValue="value"
            class="w-64"
            data-testid="select-retake-policy"
            @change="saveRetakePolicy"
          />
        </div>
      </div>
    </div>

    <!-- Branding Settings -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-1">Branding</h2>
      <p class="text-xs text-gray-400 mb-2">Customise how your school portal appears to users.</p>

      <div class="space-y-3">
        <!-- App Name -->
        <div>
          <label for="branding_app_name" class="block text-sm font-medium text-gray-600 mb-1">
            App Name
          </label>
          <InputText
            id="branding_app_name"
            v-model="brandingSettings.app_name"
            class="w-full"
            placeholder="e.g. ABC School Portal"
          />
        </div>

        <!-- Primary Color -->
        <div>
          <label for="branding_primary_color" class="block text-sm font-medium text-gray-600 mb-1">
            Primary Color
          </label>
          <div class="flex items-center gap-3">
            <input
              id="branding_primary_color"
              type="color"
              v-model="brandingSettings.primary_color"
              class="w-10 h-10 rounded border border-gray-300 cursor-pointer"
            />
            <InputText
              v-model="brandingSettings.primary_color"
              class="w-32"
              placeholder="#2B5740"
            />
          </div>
        </div>

        <!-- Accent Color -->
        <div>
          <label for="branding_accent_color" class="block text-sm font-medium text-gray-600 mb-1">
            Accent Color
          </label>
          <div class="flex items-center gap-3">
            <input
              id="branding_accent_color"
              type="color"
              v-model="brandingSettings.accent_color"
              class="w-10 h-10 rounded border border-gray-300 cursor-pointer"
            />
            <InputText
              v-model="brandingSettings.accent_color"
              class="w-32"
              placeholder="#F6B93B"
            />
            <Button
              v-if="brandingSettings.accent_color"
              icon="pi pi-times"
              text
              severity="secondary"
              size="small"
              aria-label="Clear accent color"
              @click="brandingSettings.accent_color = ''"
            />
          </div>
          <p class="text-xs text-gray-400 mt-1">
            Optional. Used as a secondary highlight alongside the primary color.
          </p>
        </div>

        <!-- Favicon URL -->
        <div>
          <label for="branding_favicon_url" class="block text-sm font-medium text-gray-600 mb-1">
            Favicon URL
          </label>
          <InputText
            id="branding_favicon_url"
            v-model="brandingSettings.favicon_url"
            class="w-full"
            placeholder="https://cdn.example.com/favicon.png"
          />
        </div>

        <!-- Login Background URL -->
        <div>
          <label for="branding_login_bg_url" class="block text-sm font-medium text-gray-600 mb-1">
            Login Background Image URL
          </label>
          <InputText
            id="branding_login_bg_url"
            v-model="brandingSettings.login_bg_url"
            class="w-full"
            placeholder="https://cdn.example.com/campus.jpg"
          />
          <p class="text-xs text-gray-400 mt-1">
            Full-bleed image behind the login form. Recommended: landscape ~1920×1080, under 500KB.
          </p>
          <img
            v-if="brandingSettings.login_bg_url"
            :src="brandingSettings.login_bg_url"
            alt="Login background preview"
            class="mt-3 h-24 w-48 object-cover rounded border border-gray-200"
            @error="($event.target as HTMLImageElement).style.display = 'none'"
          />
        </div>

        <!-- Hide GradeVerse Branding -->
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-700">Hide GradeVerse branding</p>
            <p class="text-xs text-gray-400">Remove GradeVerse references from the portal footer and public forms.</p>
          </div>
          <InputSwitch v-model="brandingSettings.hide_gradeverse_branding" />
        </div>

        <div class="pt-2">
          <Button
            label="Save Branding"
            icon="pi pi-palette"
            class="bg-primary text-white"
            :loading="isSavingBranding"
            size="small"
            @click="saveBrandingSettings"
          />
        </div>
      </div>
    </div>

    <!-- Partner / Affiliate Branding -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <h2 class="text-base font-semibold text-gray-800 mb-1">Partner / Affiliate</h2>
      <p class="text-xs text-gray-400 mb-2">
        Shown as "(IN COLLABORATION WITH …)" alongside your logo on the broadsheet and summary sheet PDFs.
      </p>

      <div class="flex flex-col sm:flex-row items-center gap-3">
        <!-- Affiliate Logo Preview -->
        <div
          class="relative w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50 hover:border-primary transition-colors cursor-pointer group"
          @click="triggerAffiliateLogoInput"
          @drop.prevent="handleAffiliateLogoDrop"
          @dragover.prevent
          @dragenter.prevent="isAffiliateDragging = true"
          @dragleave.prevent="isAffiliateDragging = false"
          :class="{ 'border-primary bg-primary/10': isAffiliateDragging }"
        >
          <img
            v-if="affiliateLogoPreview"
            :src="affiliateLogoPreview"
            alt="Affiliate Logo"
            class="w-full h-full object-cover"
          />
          <div v-else class="flex flex-col items-center text-gray-400">
            <i class="pi pi-image text-xl mb-1"></i>
            <span class="text-xs">No logo</span>
          </div>
          <!-- Overlay on hover -->
          <div
            class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"
          >
            <i class="pi pi-camera text-white text-lg"></i>
          </div>
          <input
            ref="affiliateLogoInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleAffiliateLogoSelect"
          />
        </div>

        <div class="flex-1 w-full text-center sm:text-left">
          <p class="text-sm text-gray-600 mb-2">
            Click or drag an image to upload your partner / affiliate logo.
          </p>
          <p class="text-xs text-gray-400">
            Recommended: Square image, max 2MB (JPG, PNG, WebP)
          </p>
          <div class="flex flex-wrap items-center gap-2 mt-3 justify-center sm:justify-start">
            <Button
              v-if="selectedAffiliateLogoFile"
              label="Upload Logo"
              icon="pi pi-upload"
              class="bg-primary text-white text-sm"
              :loading="isUploadingAffiliateLogo"
              @click="handleUploadAffiliateLogo"
              size="small"
            />
            <Button
              v-if="affiliateLogoPreview && !selectedAffiliateLogoFile"
              label="Remove"
              icon="pi pi-trash"
              severity="danger"
              outlined
              class="text-sm"
              :loading="isRemovingAffiliateLogo"
              @click="handleRemoveAffiliateLogo"
              size="small"
            />
          </div>
        </div>
      </div>

      <!-- Affiliate Name -->
      <div class="mt-3">
        <label for="affiliate_name" class="block text-sm font-medium text-gray-600 mb-1">
          Affiliate Name
        </label>
        <div class="flex items-center gap-2">
          <InputText
            id="affiliate_name"
            v-model="affiliateName"
            class="w-full"
            placeholder="e.g. The University of Example"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            class="bg-primary text-white whitespace-nowrap"
            :loading="isSavingAffiliateName"
            size="small"
            @click="handleSaveAffiliateName"
          />
        </div>
      </div>
    </div>

    <!-- Change Password Section -->
    <ChangePassword />
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import ChangePassword from './ChangePassword.vue';
import { useFormErrors } from '@/composables/useFormErrors';
import { LocalStorageKeys } from '@/constants/LocalStorageKey.constant';
import { useUpdateSchoolMutation, useUploadSchoolLogoMutation, useUpdateSchoolSettingsMutation } from '@/types/vue-apollo-helpers';
import { useBranding } from '@/composables/useBranding';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useAffiliateBranding } from '@/composables/mutations/school/useAffiliateBranding';

import { extractGraphQLError } from '@/utils/graphqlError';
const { school: schoolData, refetchSchool } = useSchoolQuery();
const school = ref({
  name: '',
  email: '',
  mobile: '',
  website: '',
  address: '',
  attendance_type: 'DAILY',
});
const toast = useToast();
const isSubmitting = ref(false);

// Logo upload state
const logoInput = ref<HTMLInputElement | null>(null);
const logoPreview = ref<string | null>(null);
const selectedLogoFile = ref<File | null>(null);
const isUploadingLogo = ref(false);
const isDragging = ref(false);

// Affiliate (partner) logo + name state
const { uploadAffiliateLogo, removeAffiliateLogo, validateAffiliateLogo } = useAffiliateBranding();
const affiliateLogoInput = ref<HTMLInputElement | null>(null);
const affiliateLogoPreview = ref<string | null>(null);
const selectedAffiliateLogoFile = ref<File | null>(null);
const isUploadingAffiliateLogo = ref(false);
const isRemovingAffiliateLogo = ref(false);
const isAffiliateDragging = ref(false);
const affiliateName = ref('');
const isSavingAffiliateName = ref(false);

const attendanceOptions = [
  { label: 'Daily (Whole Day)', value: 'DAILY' },
  { label: 'Session Based (Morning/Afternoon)', value: 'PER_SESSION' },
];

// Feature settings (must be declared before the watch that references it)
const allowStudentPhotoUpload = ref(false);
const showScrollbars = ref(false);
// Course registration controls (tertiary only). Open defaults TRUE — absence
// of the setting means registration is available, mirroring the backend.
const courseRegistrationOpen = ref(true);
const requireFinancialClearance = ref(false);
const retakePolicy = ref<'later_only' | 'same_and_later'>('later_only');
const retakePolicyOptions = [
  { label: 'Later semester only', value: 'later_only' },
  { label: 'Same or later semester', value: 'same_and_later' },
];
const { isTertiary } = useSchoolFeatures();

const brandingSettings = ref({
  app_name: '',
  primary_color: '#2B5740',
  accent_color: '',
  favicon_url: '',
  login_bg_url: '',
  hide_gradeverse_branding: false,
});
const isSavingBranding = ref(false);

watch(
  schoolData,
  (data) => {
    if (data) {
      school.value = {
        name: data.name || '',
        email: data.email || '',
        mobile: data.mobile || '',
        website: data.website || '',
        address: data.address || '',
        attendance_type: data.attendance_type || 'DAILY',
      };
      if (data.logo) {
        logoPreview.value = data.logo;
      }
      affiliateLogoPreview.value = data.affiliate_logo || null;
      affiliateName.value = data.affiliate_name || '';
      const dataWithSettings = data as {
        settings?: {
          allow_student_photo_upload?: boolean;
          show_scrollbars?: boolean;
          branding?: Record<string, unknown>;
          features?: { course_registration_enabled?: boolean; course_reg_financial_clearance?: boolean };
          retake_policy?: 'later_only' | 'same_and_later';
        };
      };
      allowStudentPhotoUpload.value = !!dataWithSettings.settings?.allow_student_photo_upload;
      showScrollbars.value = !!dataWithSettings.settings?.show_scrollbars;
      courseRegistrationOpen.value = dataWithSettings.settings?.features?.course_registration_enabled !== false;
      requireFinancialClearance.value = !!dataWithSettings.settings?.features?.course_reg_financial_clearance;
      retakePolicy.value = dataWithSettings.settings?.retake_policy === 'same_and_later' ? 'same_and_later' : 'later_only';
      const branding = dataWithSettings.settings?.branding || {} as Record<string, unknown>;
      brandingSettings.value = {
        app_name: branding.app_name || '',
        primary_color: branding.primary_color || '#2B5740',
        accent_color: branding.accent_color || '',
        favicon_url: branding.favicon_url || '',
        login_bg_url: branding.login_bg_url || '',
        hide_gradeverse_branding: !!branding.hide_gradeverse_branding,
      };
      localStorage.setItem(LocalStorageKeys.schoolId, data.id || '');
    }
  },
  { immediate: true },
);

const { mutate: updateSchoolMutation } = useUpdateSchoolMutation();
const { mutate: uploadLogoMutation } = useUploadSchoolLogoMutation();
const { mutate: updateSettingsMutation } = useUpdateSchoolSettingsMutation();
const { updateBranding } = useBranding();

async function saveBrandingSettings() {
  try {
    isSavingBranding.value = true;
    await updateSettingsMutation({
      settings: { branding: brandingSettings.value },
    });
    updateBranding({
      app_name: brandingSettings.value.app_name || null,
      primary_color: brandingSettings.value.primary_color || null,
      accent_color: brandingSettings.value.accent_color || null,
      favicon_url: brandingSettings.value.favicon_url || null,
      login_bg_url: brandingSettings.value.login_bg_url || null,
      hide_gradeverse_branding: brandingSettings.value.hide_gradeverse_branding,
    });
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Branding settings updated.', life: 3000 });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to save branding.';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    isSavingBranding.value = false;
  }
}

async function saveFeatureSettings() {
  try {
    await updateSettingsMutation({
      settings: {
        allow_student_photo_upload: allowStudentPhotoUpload.value,
        show_scrollbars: showScrollbars.value,
      },
    });
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Feature setting updated.', life: 3000 });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to save setting.';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  }
}

async function saveCourseRegistrationSettings() {
  try {
    await updateSettingsMutation({
      settings: {
        features: {
          course_registration_enabled: courseRegistrationOpen.value,
          course_reg_financial_clearance: requireFinancialClearance.value,
        },
      },
    });
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Course registration settings updated.', life: 3000 });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to save setting.';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  }
}

async function saveRetakePolicy() {
  try {
    await updateSettingsMutation({
      settings: { retake_policy: retakePolicy.value },
    });
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Retake policy updated.', life: 3000 });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to save setting.';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  }
}

const { serverErrors, handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

// Logo handling
function triggerLogoInput() {
  logoInput.value?.click();
}

function handleLogoSelect(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    setLogoFile(file);
  }
  if (logoInput.value) logoInput.value.value = '';
}

function handleLogoDrop(event: DragEvent) {
  isDragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file && file.type.startsWith('image/')) {
    setLogoFile(file);
  }
}

function setLogoFile(file: File) {
  if (file.size > 2 * 1024 * 1024) {
    toast.add({
      severity: 'warn',
      summary: 'File too large',
      detail: 'Logo must be under 2MB.',
      life: 3000,
    });
    return;
  }
  selectedLogoFile.value = file;
  const reader = new FileReader();
  reader.onload = (e) => {
    logoPreview.value = e.target?.result as string;
  };
  reader.readAsDataURL(file);
}

async function handleUploadLogo() {
  if (!selectedLogoFile.value) return;

  try {
    isUploadingLogo.value = true;
    const result = await uploadLogoMutation({ logo: selectedLogoFile.value });
    const newLogo = result?.data?.uploadSchoolLogo?.logo;
    if (newLogo) {
      logoPreview.value = newLogo;
    }
    selectedLogoFile.value = null;
    // Refetch school data so the cache is updated for other components
    refetchSchool();
    toast.add({
      severity: 'success',
      summary: 'Logo Updated',
      detail: 'School logo has been updated successfully.',
      life: 3000,
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to upload logo. Please try again.';
    toast.add({
      severity: 'error',
      summary: 'Upload Failed',
      detail: message,
      life: 3000,
    });
  } finally {
    isUploadingLogo.value = false;
  }
}

// Affiliate (partner) logo handling
function triggerAffiliateLogoInput() {
  affiliateLogoInput.value?.click();
}

function handleAffiliateLogoSelect(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    setAffiliateLogoFile(file);
  }
  if (affiliateLogoInput.value) affiliateLogoInput.value.value = '';
}

function handleAffiliateLogoDrop(event: DragEvent) {
  isAffiliateDragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (file && file.type.startsWith('image/')) {
    setAffiliateLogoFile(file);
  }
}

function setAffiliateLogoFile(file: File) {
  const error = validateAffiliateLogo(file);
  if (error) {
    toast.add({ severity: 'warn', summary: 'Invalid file', detail: error, life: 3000 });
    return;
  }
  selectedAffiliateLogoFile.value = file;
  const reader = new FileReader();
  reader.onload = (e) => {
    affiliateLogoPreview.value = e.target?.result as string;
  };
  reader.readAsDataURL(file);
}

async function handleUploadAffiliateLogo() {
  if (!selectedAffiliateLogoFile.value) return;

  try {
    isUploadingAffiliateLogo.value = true;
    const result = await uploadAffiliateLogo(selectedAffiliateLogoFile.value);
    const newLogo = result?.data?.uploadSchoolAffiliateLogo?.affiliate_logo;
    if (newLogo) {
      affiliateLogoPreview.value = newLogo;
    }
    selectedAffiliateLogoFile.value = null;
    refetchSchool();
    toast.add({
      severity: 'success',
      summary: 'Logo Updated',
      detail: 'Affiliate logo has been updated successfully.',
      life: 3000,
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to upload affiliate logo. Please try again.';
    toast.add({
      severity: 'error',
      summary: 'Upload Failed',
      detail: message,
      life: 3000,
    });
  } finally {
    isUploadingAffiliateLogo.value = false;
  }
}

async function handleRemoveAffiliateLogo() {
  try {
    isRemovingAffiliateLogo.value = true;
    await removeAffiliateLogo();
    affiliateLogoPreview.value = null;
    selectedAffiliateLogoFile.value = null;
    refetchSchool();
    toast.add({
      severity: 'success',
      summary: 'Logo Removed',
      detail: 'Affiliate logo has been removed.',
      life: 3000,
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to remove affiliate logo. Please try again.';
    toast.add({
      severity: 'error',
      summary: 'Remove Failed',
      detail: message,
      life: 3000,
    });
  } finally {
    isRemovingAffiliateLogo.value = false;
  }
}

async function handleSaveAffiliateName() {
  const schoolId = localStorage.getItem(LocalStorageKeys.schoolId);
  if (!schoolId) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'School ID not found.', life: 3000 });
    return;
  }

  try {
    isSavingAffiliateName.value = true;
    await updateSchoolMutation({
      id: schoolId,
      updateSchoolInput: { affiliateName: affiliateName.value },
    });
    refetchSchool();
    toast.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'Affiliate name updated.',
      life: 3000,
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to save affiliate name.';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
  } finally {
    isSavingAffiliateName.value = false;
  }
}

// School update
async function handleUpdateSchool() {
  clearErrors();
  const schoolId = localStorage.getItem(LocalStorageKeys.schoolId);
  if (!schoolId) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'School ID not found.',
      life: 3000,
    });
    return;
  }

  try {
    isSubmitting.value = true;
    await updateSchoolMutation({
      id: schoolId,
      updateSchoolInput: {
        schoolName: school.value.name,
        schoolEmail: school.value.email,
        schoolMobile: school.value.mobile,
        schoolAddress: school.value.address,
        website: school.value.website,
        attendance_type: school.value.attendance_type,
      },
    });
    toast.add({
      severity: 'success',
      summary: 'Updated',
      detail: 'School updated successfully!',
      life: 3000,
    });
  } catch (error) {
    handleServerErrors(error);
    if (Object.keys(serverErrors.value).length === 0) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: extractGraphQLError(error, 'Update failed. Try again.'),
        life: 3000,
      });
    }
  } finally {
    isSubmitting.value = false;
  }
}
</script>
